/**
 * Composable for theme management
 * Handles theme switching, persistence, and application
 */

import { ref, computed, watch } from 'vue';
import { synapseeTheme } from '../themes/synapsee.js';
import { indigoDreamTheme } from '../themes/indigo-dream.js';
import { floraTheme } from '../themes/flora.js';
import { figmaTheme } from '../themes/figma.js';
import { adobeTheme } from '../themes/adobe.js';
import { adobeDarkTheme } from '../themes/adobe-dark.js';
import { synapsee2026Theme } from '../themes/synapsee-2026.js';

// Theme storage key
const THEME_STORAGE_KEY = 'synapsee-theme';

// Available themes registry
const themesRegistry = {
  synapsee: synapseeTheme,
  'indigo-dream': indigoDreamTheme,
  flora: floraTheme,
  figma: figmaTheme,
  adobe: adobeTheme,
  'adobe-dark': adobeDarkTheme,
  'synapsee-2026': synapsee2026Theme,
};

// Global theme state
const currentThemeName = ref(
  localStorage.getItem(THEME_STORAGE_KEY) || 'synapsee-2026'
);

/**
 * Composable for theme management
 */
export function useTheme() {
  // Get current theme object
  const currentTheme = computed(() => {
    return themesRegistry[currentThemeName.value];
  });

  // Get list of available themes
  const availableThemes = computed(() => {
    return Object.entries(themesRegistry).map(([id, theme]) => ({
      id,
      name: theme.label,
      description: theme.description,
      isDark: theme.isDark,
    }));
  });

  /**
   * Apply theme to Vuetify and document
   */
  const applyTheme = (themeName) => {
    const theme = themesRegistry[themeName];

    if (!theme) {
      console.warn(`Theme "${themeName}" not found`);
      return;
    }

    // Update CSS variables on document root
    const root = document.documentElement;

    // Colors - convert camelCase to kebab-case
    try {
      Object.entries(theme.colors).forEach(([key, value]) => {
        // Convert camelCase to kebab-case: primaryLight → primary-light
        const kebabKey = key.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
        root.style.setProperty(`--color-${kebabKey}`, value);
      });
    } catch (error) {
      console.error(`❌ Error applying theme colors:`, error);
    }

    // Spacing - convert camelCase to kebab-case
    Object.entries(theme.spacing).forEach(([key, value]) => {
      const kebabKey = key.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
      root.style.setProperty(`--spacing-${kebabKey}`, value);
    });

    // Border radius - convert camelCase to kebab-case
    Object.entries(theme.borderRadius).forEach(([key, value]) => {
      const kebabKey = key.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
      root.style.setProperty(`--radius-${kebabKey}`, value);
    });

    // Shadows - convert camelCase to kebab-case
    Object.entries(theme.shadows).forEach(([key, value]) => {
      const kebabKey = key.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
      root.style.setProperty(`--shadow-${kebabKey}`, value);
    });

    // Component-specific tokens (sidebar, button, etc) - convert camelCase to kebab-case
    if (theme.components) {
      Object.entries(theme.components).forEach(([componentName, tokens]) => {
        const kebabComponentName = componentName.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
        Object.entries(tokens).forEach(([tokenKey, value]) => {
          // Only set CSS variables for non-object values
          if (typeof value === 'string' || typeof value === 'number') {
            const kebabTokenKey = tokenKey.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
            root.style.setProperty(`--${kebabComponentName}-${kebabTokenKey}`, value);
          }
        });
      });
    }

    // Update body class for dark mode
    if (theme.isDark) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }

    // Store preference
    localStorage.setItem(THEME_STORAGE_KEY, themeName);
  };

  /**
   * Switch to a specific theme
   */
  const switchTheme = (themeName) => {
    if (!themesRegistry[themeName]) {
      console.warn(`Theme "${themeName}" not found`);
      return;
    }

    currentThemeName.value = themeName;
    applyTheme(themeName);
  };

  /**
   * Get a specific value from the current theme
   */
  const getThemeValue = (path) => {
    const keys = path.split('.');
    let value = currentTheme.value;

    for (const key of keys) {
      if (value && typeof value === 'object') {
        value = value[key];
      } else {
        return undefined;
      }
    }

    return value;
  };

  /**
   * Get CSS variable name for a token
   */
  const getCSSVariable = (category, token) => {
    return `var(--${category}-${token})`;
  };

  // Initialize theme on first load
  watch(
    () => currentThemeName.value,
    (newTheme) => {
      applyTheme(newTheme);
    },
    { immediate: true }
  );

  return {
    currentThemeName,
    currentTheme,
    availableThemes,
    switchTheme,
    applyTheme,
    getThemeValue,
    getCSSVariable,
  };
}

/**
 * Composable for using theme tokens in components
 */
export function useThemeTokens() {
  const { getThemeValue, currentTheme } = useTheme();

  return {
    // Colors
    getColor: (name) => getThemeValue(`colors.${name}`),

    // Spacing
    getSpacing: (name) => getThemeValue(`spacing.${name}`),

    // Typography
    getTypography: (name) => getThemeValue(`typography.styles.${name}`),

    // Border radius
    getBorderRadius: (name) => getThemeValue(`borderRadius.${name}`),

    // Shadows
    getShadow: (name) => getThemeValue(`shadows.${name}`),

    // Component tokens
    getComponentTokens: (component) => getThemeValue(`components.${component}`),

    // Entire theme
    theme: currentTheme,
  };
}
